import React, { useMemo, useState } from 'react'
import {
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  FormControl,
  Box,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Tooltip as MuiTooltip,
} from '@mui/material'
import { ExpandMore, Info, OpenInNew } from '@mui/icons-material'
import useAlert from '../hooks/useAlert'
import Dropdown from './components/Dropdown'
import AccordionHeader from './components/AccordionHeader'
import InputGroup from './components/InputGroup'
import HalfContainer from './components/HalfContainer'
import AccountOwnerSelect from './components/AccountOwnerSelect'
import {
  setPrimaryAccountOwnerUtil,
  setSecondaryAccountOwnersUtil,
} from '../utils/accountOwner'
import { states } from '../register/states'
import AddressAutocomplete from '../common/AddressAutocomplete'
import Tooltip from '../common/Tooltip'
import { formatToPhone } from '../utils/string'

const CustomerInfo = (props) => {
  const {
    updateCustomer,
    setCustomer,
    customer,
    types,
    dropdowns,
    isNew,
    saveCustomer,
    reset,
    customerStatusTypes,
    companyConfig,
    handleLocationChange,
    handleBulkLocationChange,
    primaryLocation,
    expandedAccordions,
    setExpandedAccordions,
    renderedSaveButton,
  } = props

  const expanded = expandedAccordions.info
  const setExpanded = (val) =>
    setExpandedAccordions((prev) => ({ ...prev, info: val }))

  const [editing, setEditing] = useState(true)

  const primaryAccountOwner =
    customer.accountOwners?.find((ao) => ao.isPrimary) || null

  const secondaryAccountOwners =
    customer.accountOwners?.filter((ao) => !ao.isPrimary) || []

  const setSecondaryAccountOwners = (newSecondaryAccountOwners) => {
    const accountOwners = setSecondaryAccountOwnersUtil(
      newSecondaryAccountOwners,
      customer.accountOwners
    )

    setCustomer((c) => ({ ...c, accountOwners }))
  }

  const setPrimaryAccountOwner = (identity) => {
    const accountOwners = setPrimaryAccountOwnerUtil(
      identity,
      customer.accountOwners
    )

    setCustomer((c) => ({ ...c, accountOwners }))
  }

  const { createAlert, createGenericErrorAlert } = useAlert()

  const handleSaveCustomer = () => {
    saveCustomer({ saveCustomerInfo: true })
      .then(() => {
        // setEditing(false)
        createAlert({ message: 'Account Information saved successfully' })
      })
      .catch(() => {
        createGenericErrorAlert()
      })
  }

  const isBusiness = customer.typeId === 1
  const isPartner = customer.typeId === 3

  // if businessTypeId === 2, (B2C) put Individual(2) first. Always put Partner(3) last
  const customerTypes = useMemo(() => {
    return types.customerTypes
      .filter((t) => t.id !== 4)
      .sort((a, b) => {
        if (a.id === 3) return 1
        if (b.id === 3) return -1

        if (companyConfig.businessTypeId === 2) return b.id - a.id
        return a.id - b.id
      })
  }, [types?.customerTypes])

  const getOutcomeReasonDropdown = () => {
    const won = dropdowns.find((dd) => dd.key === 'wonOutcomeReason')
    const lost = dropdowns.find((dd) => dd.key === 'lostOutcomeReason')

    const wonOptions = (won?.options || []).map((o) => ({
      ...o,
      name: `Won - ${o.name}`,
    }))
    const lostOptions = (lost?.options || []).map((o) => ({
      ...o,
      name: `Lost - ${o.name}`,
    }))

    return {
      key: 'outcomeReason',
      name: 'Deal Outcome/Reason',
      options: wonOptions.concat(lostOptions),
    }
  }

  const getDropdownByKey = (key, required) => {
    let d
    if (key === 'outcomeReason') {
      d = getOutcomeReasonDropdown()
    } else {
      d = dropdowns.find((dd) => dd.key === key)
    }

    if (!d) return <></>

    const value = customer.dropdowns?.[`${d.key}TypeId`]

    return (
      <HalfContainer key={d.key}>
        <Dropdown
          error={!value}
          required={required}
          dropdown={d}
          label={d.name}
          onChange={(value) => {
            setCustomer((c) => ({
              ...c,
              dropdowns: {
                ...(c.dropdowns || {}),
                [`${d.key}TypeId`]: value,
              },
            }))
          }}
          customer={customer}
          disabled={!editing}
          options={d.options}
          value={value}
        />
      </HalfContainer>
    )
  }

  return (
    <Accordion
      elevation={8}
      sx={{ maxWidth: '1250px' }}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography
          sx={{ fontSize: '20px', display: 'flex', alignItems: 'center' }}
        >
          <b>Account Information</b>
          <Tooltip
            infoText={
              'This section keeps the primary information about the Business, Individual, or Partner and the status of the account. Fields with an asterisk are required.'
            }
          />
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <AccordionHeader
          hide={isNew}
          isEditing={editing}
          setIsEditing={setEditing}
          onCancel={() => reset({ resetCustomerInfo: true })}
          onSave={handleSaveCustomer}
        />

        <Box sx={{ mb: 1 }}>
          <FormControl margin='dense'>
            <ToggleButtonGroup
              value={customer.typeId}
              exclusive
              onChange={(e) => updateCustomer('typeId', e, true)}
              color='primary'
            >
              {customerTypes.map((type, i) => (
                <ToggleButton key={i} value={type.id}>
                  {type.name}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </FormControl>
        </Box>

        <InputGroup sx={{ mb: 4, justifyContent: 'stretch', mr: -1 }}>
          <HalfContainer>
            <TextField
              error={!customer.name}
              sx={{ minWidth: '300px', notchedOutline: { border: 'red' } }}
              margin='dense'
              fullWidth
              label={`${isBusiness ? 'Business ' : ''}Name`}
              required
              onChange={(e) => updateCustomer('name', e)}
              disabled={!editing}
              value={customer.name}
            />
          </HalfContainer>

          <HalfContainer>
            <TextField
              fullWidth
              sx={{ mr: 1 }}
              value={formatToPhone(customer.primaryPhone)}
              label={`${isBusiness ? 'Business ' : 'Primary '}Phone`}
              onChange={(e) => updateCustomer('primaryPhone', e)}
              margin='dense'
              disabled={!editing}
            />
          </HalfContainer>

          <HalfContainer>
            {/* <TextField
              sx={{ minWidth: '300px', mr: 1 }}
              margin='dense'
              fullWidth
              label={`${isBusiness ? 'Business ' : ''}Address`}
              onChange={(e) =>
                handleLocationChange('streetAddress', e.target.value)
              }
              disabled={!editing}
              value={primaryLocation?.streetAddress || ''}
            /> */}
            <AddressAutocomplete
              isBusiness={isBusiness}
              handleBulkLocationChange={handleBulkLocationChange}
              value={primaryLocation?.streetAddress || ''}
              setValue={(value) => handleLocationChange('streetAddress', value)}
            />
          </HalfContainer>
          <HalfContainer>
            <TextField
              sx={{ minWidth: '300px', mr: 1 }}
              margin='dense'
              fullWidth
              label='City'
              onChange={(e) => handleLocationChange('city', e.target.value)}
              disabled={!editing}
              value={primaryLocation?.city || ''}
            />
          </HalfContainer>
          <HalfContainer>
            <TextField
              sx={{ minWidth: '300px', mr: 1 }}
              fullWidth
              label='State'
              onChange={(e) => {
                handleLocationChange('stateOrProvince', e.target.value)
              }}
              disabled={!editing}
              value={primaryLocation?.stateOrProvince || ''}
              margin='dense'
              select
            >
              {states.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </HalfContainer>

          <HalfContainer>
            <TextField
              sx={{ minWidth: '300px', mr: 1 }}
              margin='dense'
              fullWidth
              label='Zip Code'
              onChange={(e) => handleLocationChange('zipCode', e.target.value)}
              disabled={!editing}
              value={primaryLocation?.zipCode || ''}
            />
          </HalfContainer>

          <HalfContainer>
            <TextField
              fullWidth
              value={customer.primaryEmail}
              label={`${isBusiness ? 'Business ' : 'Primary '} Email`}
              onChange={(e) => updateCustomer('primaryEmail', e)}
              margin='dense'
              disabled={!editing}
            />
          </HalfContainer>
          <HalfContainer>
            <TextField
              fullWidth
              value={formatToPhone(customer.faxNumber)}
              label='Fax Number'
              onChange={(e) => updateCustomer('faxNumber', e)}
              margin='dense'
              disabled={!editing}
            />
          </HalfContainer>

          {getDropdownByKey('industry', false)}

          <AccountOwnerSelect
            disabled={!editing}
            primaryAccountOwner={primaryAccountOwner}
            setPrimaryAccountOwner={setPrimaryAccountOwner}
            secondaryAccountOwners={secondaryAccountOwners}
            setSecondaryAccountOwners={setSecondaryAccountOwners}
          />

          {customer.typeId === 1 && (
            <HalfContainer>
              <TextField
                fullWidth
                margin='dense'
                label='Website'
                onChange={(e) => updateCustomer('website', e)}
                value={customer.website}
                disabled={!editing}
                InputProps={{
                  endAdornment: (
                    <MuiTooltip title={'Go to ' + customer.website}>
                      <IconButton
                        color={'primary'}
                        sx={{ flexGrow: 0 }}
                        onClick={() => {
                          window.open(
                            customer.website.match(/^https?:/)
                              ? customer.website
                              : '//' + customer.website,
                            '_blank'
                          )
                        }}
                      >
                        <OpenInNew />
                      </IconButton>
                    </MuiTooltip>
                  ),
                }}
              />
            </HalfContainer>
          )}
        </InputGroup>
        <InputGroup>
          <HalfContainer>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                error={!isPartner && !customer.statusTypeId}
                required={!isPartner}
                margin='dense'
                fullWidth
                label={'Status'}
                value={isPartner ? 'partner' : customer.statusTypeId || ''}
                onChange={(e) => updateCustomer('statusTypeId', e)}
                select
                disabled={!editing || isPartner}
                InputProps={{
                  startAdornment: (
                    <MuiTooltip
                      sx={{ mr: 1 }}
                      title={
                        <Typography sx={{ fontSize: '18px' }}>
                          Certain sections of the status field are intended for
                          automation, and specific options may be inaccessible
                          until certain actions, like creating a new opportunity
                          or finalizing a deal, are completed.
                        </Typography>
                      }
                    >
                      <Info color='primary'></Info>
                    </MuiTooltip>
                  ),
                }}
              >
                <MenuItem value={''} />
                {isPartner && <MenuItem value={'partner'}>Partner</MenuItem>}
                {!isPartner &&
                  customerStatusTypes.map((t, i) => {
                    const allowedOptions = [1, 2]

                    const notDisabled =
                      customer.statusTypeId === t.id ||
                      (allowedOptions.includes(t.id) &&
                        (allowedOptions.includes(customer.statusTypeId) ||
                          !customer.statusTypeId ||
                          customer.statusTypeId === 5))

                    return (
                      <MenuItem disabled={!notDisabled} key={i} value={t.id}>
                        {t.name}
                      </MenuItem>
                    )
                  })}
              </TextField>
            </Box>
          </HalfContainer>

          {getDropdownByKey('leadSource')}

          <HalfContainer>
            <TextField
              margin='dense'
              fullWidth
              label={'Importance'}
              value={customer.importanceTypeId || ''}
              onChange={(e) => updateCustomer('importanceTypeId', e)}
              select
              disabled={!editing}
            >
              <MenuItem value={''} />
              {types.importanceTypes.map((o, i) => (
                <MenuItem key={i} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </TextField>
          </HalfContainer>

          {getDropdownByKey('nextStep')}
          <HalfContainer>
            <TextField
              sx={{ mr: 1 }}
              margin='dense'
              fullWidth
              label='Product of Interest'
              onChange={(e) => updateCustomer('productOfInterest', e)}
              value={customer.productOfInterest}
              disabled={!editing}
            />
          </HalfContainer>

          {getDropdownByKey('outcomeReason')}
        </InputGroup>

        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mr: -2 }}
        >
          {renderedSaveButton && renderedSaveButton}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

export default CustomerInfo
